import {
  BillingBusinessLicense,
  BillingLicensePlans,
} from '@kizen/api/billing';
import { Button } from '@kizen/kds/Button';
import { Spacer } from '@kizen/kds/Spacer';
import { Typography } from '@kizen/kds/Typography';
import { useTranslation } from 'react-i18next';
import Bullet from './assets/bullet.svg?react';
import { SmallInput, TranslatedHeader } from './styles';
import { useEffect, useMemo, useState } from 'react';
import { getUnifiedPlan, interpolateValues } from '../util';

type PlanCardProps = {
  plan?: BillingLicensePlans['license_templates'][number];
  currentPlan?: BillingBusinessLicense[number];
  period: string;
  unit?: string;
  isCurrentPlan?: boolean;
  isSelectedPlan?: boolean;
  isLegacy?: boolean;
  onSelectPlan?: () => void;
  numberInput?: boolean;
  numberInputLabel?: string;
  numberInputValue?: number;
  onNumberInputValueChange?: (value: number) => void;
  selectable?: boolean;
  isDowngradePlan?: boolean;
  onCancelDowngrade?: () => void;
  downgradeDate?: string;
  pendingDowngradeMaxUsers?: number;
};

export const PlanCard = (props: PlanCardProps) => {
  const {
    plan: planProp,
    currentPlan: currentPlanProp,
    unit,
    period,
    isCurrentPlan,
    isSelectedPlan,
    isLegacy,
    onSelectPlan,
    numberInput,
    numberInputLabel,
    numberInputValue,
    onNumberInputValueChange,
    selectable = true,
    isDowngradePlan,
    onCancelDowngrade,
    downgradeDate,
    pendingDowngradeMaxUsers,
  } = props;

  const { t } = useTranslation();

  const [inputTextValue, setInputTextValue] = useState(
    String(numberInputValue)
  );

  useEffect(() => {
    setInputTextValue(String(numberInputValue));
  }, [numberInputValue]);

  const plan = useMemo(() => {
    return getUnifiedPlan(planProp, currentPlanProp);
  }, [currentPlanProp, planProp]);

  const isFree = !plan?.price || plan.price.amount === 0;

  if (!plan) {
    return null;
  }

  const hasPendingDowngradeUserCount =
    typeof pendingDowngradeMaxUsers === 'number';

  const selectedPlanDowngradeText =
    isDowngradePlan && hasPendingDowngradeUserCount
      ? t('Cancel User Change')
      : t('Cancel Downgrade');

  const showPendingDowngradeUserCountLabel =
    hasPendingDowngradeUserCount &&
    downgradeDate &&
    pendingDowngradeMaxUsers !== Number(inputTextValue);

  const showUserSelectionUI = !isFree && numberInput && isSelectedPlan;

  return (
    <div
      key={plan.id}
      className={`relative border-1 rounded-lg p-4 flex flex-col ${isSelectedPlan ? 'border-border-active ' : 'border-gray-500 '}`}
    >
      <div className="flex items-center">
        <Typography size="lg">{plan.name}</Typography>
        <span className="ml-auto flex items-end">
          <Typography size="lg">{plan.price?.symbol}</Typography>
          <Typography size="lg">{plan.price?.amount}</Typography>
          {plan.price?.amount === 0 ? (
            <Typography size="md">/{period}</Typography>
          ) : (
            <Typography size="md">
              {unit ? `/${unit}` : ''}/{period}
            </Typography>
          )}
        </span>
      </div>
      <Spacer size={20} />
      <Typography weight="semibold">
        {plan?.ui_config?.highlights_section?.section_label ??
          t('Plan Highlights')}
      </Typography>
      <Spacer size={10} />
      {plan?.ui_config?.highlights_section?.items?.map((item: string) => {
        return (
          <div key={item}>
            <div className="flex gap-x-10">
              <Bullet className="min-w-[20px] min-h-[20px] self-start" />
              <Typography size="md" className="pt-2">
                {interpolateValues(item, plan)}
              </Typography>
            </div>
            <Spacer size={10} />
          </div>
        );
      })}
      <Spacer size={10} />
      <Typography weight="semibold">
        {plan?.ui_config?.limits_section?.section_label ?? t('Plan Limits')}
      </Typography>
      <Spacer size={10} />
      {plan?.ui_config?.limits_section?.items?.map((item: string) => {
        return (
          <div key={item}>
            <div className="flex gap-x-10">
              <Bullet className="min-w-[20px] min-h-[20px] self-start" />
              <Typography size="md" className="pt-2">
                {interpolateValues(item, plan)}
              </Typography>
            </div>
            <Spacer size={10} />
          </div>
        );
      })}
      <Spacer size={10} />
      <div className="flex justify-center mt-auto">
        {isSelectedPlan ? (
          <Button
            variant="standard"
            outline={isDowngradePlan}
            color={isDowngradePlan ? 'warning' : 'secondary'}
            onClick={isDowngradePlan ? onCancelDowngrade : onSelectPlan}
            disabled={isDowngradePlan ? false : !selectable}
          >
            {isDowngradePlan ? selectedPlanDowngradeText : t('Selected Plan')}
          </Button>
        ) : (
          <Button
            variant="standard"
            outline
            color={isDowngradePlan ? 'warning' : 'secondary'}
            onClick={isDowngradePlan ? onCancelDowngrade : onSelectPlan}
            disabled={isDowngradePlan ? false : !selectable}
          >
            {isDowngradePlan ? t('Cancel Downgrade') : t('Select Plan')}
          </Button>
        )}
      </div>
      {isCurrentPlan ? (
        <div className="absolute top-0 right-0 left-0 flex items-center justify-center">
          <TranslatedHeader className="bg-primary py-2 px-4 uppercase max-w-[90%]">
            <div>
              <Typography
                size="sm"
                weight="semibold"
                color="font-white"
                truncate
              >
                {t('Current Plan')}
                {isLegacy ? ` (${t('Legacy')})` : ''}
              </Typography>
            </div>
          </TranslatedHeader>
        </div>
      ) : isDowngradePlan && downgradeDate ? (
        <div className="absolute top-0 right-0 left-0 flex items-center justify-center">
          <TranslatedHeader className="bg-warning py-2 px-4 uppercase max-w-[90%]">
            <div>
              <Typography
                size="sm"
                weight="semibold"
                color="font-white"
                truncate
              >
                {t('Downgrade on {{date}}', {
                  date: new Date(downgradeDate).toLocaleDateString(),
                })}
                {isLegacy ? ` (${t('Legacy')})` : ''}
              </Typography>
            </div>
          </TranslatedHeader>
        </div>
      ) : null}

      {showUserSelectionUI ? (
        <div>
          <Spacer size={20} />
          <div className="flex items-center">
            <div className="flex grow">
              <Typography size="md">{numberInputLabel}</Typography>
            </div>
            <div className="w-24">
              <SmallInput
                value={inputTextValue}
                onChange={setInputTextValue}
                placeholder="3"
                disabled={hasPendingDowngradeUserCount}
                onBlur={() => {
                  if (!inputTextValue || isNaN(Number(inputTextValue))) {
                    setInputTextValue('0');
                    onNumberInputValueChange?.(0);
                  } else {
                    onNumberInputValueChange?.(Number(inputTextValue));
                  }
                }}
              />
            </div>
          </div>
          {showPendingDowngradeUserCountLabel ? (
            <>
              <Spacer size={15} />
              <Typography
                className="text-center "
                size="sm"
                fontStyle="italic"
                color={'gray-600'}
              >
                {t('Pending downgrade to {{maxUsers}} users on {{date}}', {
                  maxUsers: pendingDowngradeMaxUsers,
                  date: new Date(downgradeDate).toLocaleDateString(),
                })}
              </Typography>
            </>
          ) : null}
        </div>
      ) : null}
    </div>
  );
};
